import { FC, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Paper,
  Switch,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { X, MinusCircle, PlusCircle } from '@phosphor-icons/react';
import { Drawer } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createStoreHours } from '../../../utils/apis';
import { Day, IStoreSchedule, TimeSlot } from '../../../schemas/apis/interface';

interface StoreHoursDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedStoreId: number | null;
  tenantName: string;
  showAlert: (message: string, severity: 'success' | 'error') => void;
  existingHours?: any;
}

const StoreHoursDrawer: FC<StoreHoursDrawerProps> = ({
  open,
  onClose,
  selectedStoreId,
  tenantName,
  showAlert,
  existingHours,
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'general' | 'custom'>('general');
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // Initialize with default empty hours
  const defaultHours = useMemo(
    () => ({
      store_id: selectedStoreId || 0,
      weekly_settings: {
        Monday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Tuesday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Wednesday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Thursday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Friday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Saturday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
        Sunday: {
          is_open: false,
          time_slots: [
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
      },
      is_template: false,
    }),
    [selectedStoreId]
  );

  // Map existing hours to our format when component mounts or when existingHours changes
  useEffect(() => {
    if (
      existingHours &&
      existingHours.opening_hours &&
      Array.isArray(existingHours.opening_hours)
    ) {
      const mappedHours = { ...defaultHours };

      // Map the API response to our state format
      existingHours.opening_hours.forEach((dayData: any) => {
        const day = dayData.day_of_week as Day;
        mappedHours.weekly_settings[day] = {
          is_open: dayData.is_open,
          time_slots:
            dayData.time_slots.length > 0
              ? dayData.time_slots.map((slot: any) => ({
                  from_time: slot.from_time,
                  to_time: slot.to_time,
                }))
              : [{ from_time: '09:00', to_time: '17:00' }], // Default time slot if empty
        };
      });

      setStoreHours(mappedHours);
    } else {
      // Reset to default if no existing hours
      setStoreHours(defaultHours);
    }
  }, [existingHours, selectedStoreId, defaultHours]);

  const [storeHours, setStoreHours] = useState(defaultHours);

  const { mutateAsync: saveStoreHours, isPending: saveStoreHoursPending } =
    useMutation({
      mutationFn: (data: { formData: IStoreSchedule; store_id: string }) =>
        createStoreHours(data.formData, data.store_id, tenantName),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['storeHours', selectedStoreId, tenantName],
        });
        queryClient.invalidateQueries({
          queryKey: ['stores'],
        });
        showAlert('Store hours saved successfully', 'success');
        onClose();
      },
      onError: error => {
        showAlert('Failed to save store hours', 'error');
        console.error('Error saving store hours:', error);
      },
    });

  // Helper function to format time for input fields
  const formatTimeForInput = (time: any) => {
    if (!time) return '09:00';

    if (typeof time === 'string') {
      return time.includes(':') ? time : '09:00';
    }

    // Handle Date objects
    if (time instanceof Date) {
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    return '09:00'; // Default fallback
  };

  // Modified handleTimeChange to store time as strings
  const handleTimeChange = (
    day: Day,
    index: number,
    field: 'from_time' | 'to_time',
    value: string
  ) => {
    setStoreHours((prev: any) => {
      const currentDay = prev.weekly_settings[day] || {
        is_open: false,
        time_slots: [],
      };
      const updatedTimeSlots = currentDay.time_slots.map(
        (slot: any, i: number) =>
          i === index ? { ...slot, [field]: value } : slot
      );

      return {
        ...prev,
        weekly_settings: {
          ...prev.weekly_settings,
          [day]: {
            ...currentDay,
            time_slots: updatedTimeSlots,
          },
        },
      };
    });
  };

  // Modified addTimeSlot to use string format
  const addTimeSlot = (day: string) => {
    setStoreHours((prev: any) => ({
      ...prev,
      weekly_settings: {
        ...prev.weekly_settings,
        [day]: {
          ...prev.weekly_settings[day],
          time_slots: [
            ...prev.weekly_settings[day].time_slots,
            {
              from_time: '09:00',
              to_time: '17:00',
            },
          ],
        },
      },
    }));
  };

  const removeTimeSlot = (day: string, index: number) => {
    setStoreHours((prev: any) => {
      const currentTimeSlots = prev.weekly_settings[day].time_slots;

      // If this is the last time slot, turn off the switch for this day
      if (currentTimeSlots.length === 1) {
        return {
          ...prev,
          weekly_settings: {
            ...prev.weekly_settings,
            [day]: {
              ...prev.weekly_settings[day],
              is_open: false,
              time_slots: [],
            },
          },
        };
      }

      // Otherwise just remove the time slot
      return {
        ...prev,
        weekly_settings: {
          ...prev.weekly_settings,
          [day]: {
            ...prev.weekly_settings[day],
            time_slots: currentTimeSlots.filter(
              (_: any, i: number) => i !== index
            ),
          },
        },
      };
    });
  };

  const toggleDayOpen = (day: Day) => {
    setStoreHours((prev: any) => {
      const currentDay = prev.weekly_settings[day];

      // If the day data doesn't exist, initialize it as closed with empty slots
      const isCurrentlyOpen = currentDay?.is_open || false;

      return {
        ...prev,
        weekly_settings: {
          ...prev.weekly_settings,
          [day]: {
            is_open: !isCurrentlyOpen,
            time_slots: !isCurrentlyOpen
              ? [{ from_time: '09:00', to_time: '17:00' }] // Set default time when opening
              : [], // Empty times when closing
          },
        },
      };
    });
  };

  const handleSaveHours = async () => {
    if (!selectedStoreId) {
      showAlert('Please select a store first', 'error');
      return;
    }

    // Format data for API
    const formattedData: IStoreSchedule = {
      store_id: selectedStoreId,
      is_template: false,
      weekly_settings: {
        Monday: storeHours.weekly_settings.Monday || {
          is_open: false,
          time_slots: [],
        },
        Tuesday: storeHours.weekly_settings.Tuesday || {
          is_open: false,
          time_slots: [],
        },
        Wednesday: storeHours.weekly_settings.Wednesday || {
          is_open: false,
          time_slots: [],
        },
        Thursday: storeHours.weekly_settings.Thursday || {
          is_open: false,
          time_slots: [],
        },
        Friday: storeHours.weekly_settings.Friday || {
          is_open: false,
          time_slots: [],
        },
        Saturday: storeHours.weekly_settings.Saturday || {
          is_open: false,
          time_slots: [],
        },
        Sunday: storeHours.weekly_settings.Sunday || {
          is_open: false,
          time_slots: [],
        },
      },
    };

    (Object.keys(storeHours?.weekly_settings || {}) as Day[]).forEach(day => {
      const dayData = storeHours?.weekly_settings[day];

      if (dayData?.is_open && dayData.time_slots.length > 0) {
        formattedData.weekly_settings[day] = {
          is_open: dayData.is_open,
          time_slots: dayData.time_slots.map((slot: TimeSlot) => ({
            from_time: slot.from_time,
            to_time: slot.to_time,
          })),
        };
      }
    });

    try {
      await saveStoreHours({
        formData: formattedData,
        store_id: selectedStoreId.toString(),
      });
    } catch (error) {
      console.error('Error saving store hours:', error);
    }
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: 'general' | 'custom'
  ) => {
    setActiveTab(newValue);
  };

  // Add state for the Copy To dropdown menu
  const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [copyFromDay, setCopyFromDay] = useState<Day | null>(null);
  const [selectedDays, setSelectedDays] = useState<Record<Day, boolean>>({
    Sunday: false,
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
  });

  // Handle opening the Copy To menu
  const handleCopyToClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    day: Day
  ) => {
    setCopyMenuAnchorEl(event.currentTarget);
    setCopyFromDay(day);

    // Reset selected days
    setSelectedDays({
      Sunday: false,
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
    });
  };

  // Handle closing the Copy To menu
  const handleCopyMenuClose = () => {
    setCopyMenuAnchorEl(null);
    setCopyFromDay(null);
  };

  // Handle day selection in the dropdown
  const handleDaySelect = (day: Day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  // Handle confirming the copy operation
  const handleConfirmCopy = () => {
    if (!copyFromDay) return;

    const sourceDay = storeHours.weekly_settings[copyFromDay];

    // Create a new state object
    const updatedWeeklySettings = { ...storeHours.weekly_settings };

    // Apply the source day's settings to all selected days
    Object.entries(selectedDays).forEach(([day, isSelected]) => {
      if (isSelected && day !== copyFromDay) {
        updatedWeeklySettings[day as Day] = {
          is_open: true,
          time_slots: [...sourceDay.time_slots.map(slot => ({ ...slot }))],
        };
      }
    });

    // Update the state
    setStoreHours(prev => ({
      ...prev,
      weekly_settings: updatedWeeklySettings,
    }));

    // Close the menu
    handleCopyMenuClose();
  };

  // Add a function to handle "All Days" selection
  const handleSelectAllDays = (checked: boolean) => {
    const updatedDays = { ...selectedDays };

    // Update all days except the source day
    days.forEach(day => {
      if (day !== copyFromDay) {
        updatedDays[day as Day] = checked;
      }
    });

    setSelectedDays(updatedDays);
  };

  // Add a computed property to check if all selectable days are selected
  const areAllDaysSelected = () => {
    return days
      .filter(day => day !== copyFromDay)
      .every(day => selectedDays[day as Day]);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '400px',
          padding: '20px 24px',
          backgroundColor: '#FAFBFD',
          overflow: 'auto',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {/* Close (Cancel) Button and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': { backgroundColor: '#E8EBF1' },
              transition: 'all 0.2s ease',
            }}
          >
            <X size={18} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: 'custom.textBlue',
              letterSpacing: '-0.2px',
            }}
          >
            Store Opening Hours
          </Typography>
        </Box>

        {/* Save Button */}
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!selectedStoreId || saveStoreHoursPending}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
          onClick={handleSaveHours}
        >
          {saveStoreHoursPending ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <Divider
        sx={{
          borderColor: 'rgba(0, 0, 0, 0.08)',
          mb: 1,
          mx: -3,
        }}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mx: -3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="store hours tabs"
          sx={{
            minHeight: 'unset',
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 'unset',
              py: 1,
              px: 2,
              mx: 0.5,
            },
          }}
        >
          <Tab label="General" value="general" />
          <Tab label="Custom" value="custom" />
        </Tabs>
      </Box>

      {/* Weekly Settings Section - Only show in General tab for now */}
      {activeTab === 'general' && (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: 12,
              color: 'custom.textBlue',
              fontWeight: 500,
            }}
          >
            Weekly Settings
          </Typography>

          <Box
            sx={{
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {days.map((day: string) => (
              <Paper
                key={day}
                elevation={0}
                sx={{
                  p: '12px',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: storeHours?.weekly_settings[day as Day]
                      .is_open
                      ? '1px solid #DCE0E7'
                      : 'none',
                    pb: storeHours?.weekly_settings[day as Day].is_open
                      ? '12px'
                      : '0px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Switch
                      checked={storeHours?.weekly_settings[day as Day].is_open}
                      onChange={() => toggleDayOpen(day as Day)}
                      color="primary"
                      size="small"
                      sx={{
                        width: 36,
                        height: 20,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '2px',
                          transitionDuration: '300ms',
                          '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#6366F1',
                              opacity: 1,
                              border: 0,
                            },
                          },
                          '&.Mui-focusVisible .MuiSwitch-thumb': {
                            color: '#6366F1',
                            border: '6px solid #fff',
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          boxSizing: 'border-box',
                          width: 16,
                          height: 16,
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 26 / 2,
                          backgroundColor: '#E9E9EA',
                          opacity: 1,
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '100%',
                        color: '#6B6F80',
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>

                  {/* Copy To button */}
                  {storeHours?.weekly_settings[day as Day].is_open && (
                    <Button
                      size="small"
                      onClick={e => handleCopyToClick(e, day as Day)}
                      sx={{
                        color: 'primary.main',
                        fontSize: '12px',
                        textTransform: 'none',
                        minWidth: 'auto',
                      }}
                    >
                      Copy To ▾
                    </Button>
                  )}
                </Box>

                {storeHours?.weekly_settings[day as Day].is_open && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      mt: 2,
                    }}
                  >
                    {storeHours?.weekly_settings[day as Day].time_slots.map(
                      (slot, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <TextField
                            label="From"
                            type="time"
                            value={formatTimeForInput(slot.from_time)}
                            onChange={e =>
                              handleTimeChange(
                                day as Day,
                                index,
                                'from_time',
                                e.target.value
                              )
                            }
                            size="small"
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                fontSize: 12,
                              },
                              '& .MuiInputBase-input': {
                                fontSize: 12,
                                fontWeight: 400,
                              },
                            }}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                              htmlInput: {
                                step: 300, // 5 min steps
                              },
                            }}
                          />

                          <TextField
                            label="To"
                            type="time"
                            value={formatTimeForInput(slot.to_time)}
                            onChange={e =>
                              handleTimeChange(
                                day as Day,
                                index,
                                'to_time',
                                e.target.value
                              )
                            }
                            size="small"
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                fontSize: 12,
                              },
                              '& .MuiInputBase-input': {
                                fontSize: 12,
                                fontWeight: 400,
                              },
                            }}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                              htmlInput: {
                                step: 300, // 5 min steps
                              },
                            }}
                          />
                          <Box>
                            <IconButton
                              onClick={() => addTimeSlot(day)}
                              size="small"
                              sx={{
                                color: 'primary.main',
                                fontSize: 14,
                                fontWeight: 400,
                              }}
                            >
                              <PlusCircle size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => removeTimeSlot(day, index)}
                              size="small"
                              sx={{ color: 'primary.main' }}
                            >
                              <MinusCircle size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>

          {/* Copy To Menu */}
          <Menu
            anchorEl={copyMenuAnchorEl}
            open={Boolean(copyMenuAnchorEl)}
            onClose={handleCopyMenuClose}
            slotProps={{
              paper: {
                sx: {
                  width: '180px',
                  mt: 1,
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                },
              },
            }}
          >
            {/* All Days checkbox */}
            <MenuItem
              dense
              onClick={() => handleSelectAllDays(!areAllDaysSelected())}
              sx={{ py: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={areAllDaysSelected()}
                    size="small"
                    indeterminate={
                      Object.values(selectedDays).some(Boolean) &&
                      !areAllDaysSelected()
                    }
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'custom.textBlue',
                    }}
                  >
                    All Days
                  </Typography>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {days.map(day => (
              <MenuItem
                key={day}
                dense
                disabled={day === copyFromDay}
                onClick={() => handleDaySelect(day as Day)}
                sx={{
                  //   py: 0.5,
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDays[day as Day]}
                      size="small"
                      disabled={day === copyFromDay}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px' }}>{day}</Typography>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </MenuItem>
            ))}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 1,
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Button
                size="small"
                onClick={handleCopyMenuClose}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  color: 'text.secondary',
                  mr: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleConfirmCopy}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: '6px',
                  px: 2,
                }}
              >
                Confirm
              </Button>
            </Box>
          </Menu>
        </>
      )}

      {/* Custom Tab Content - Placeholder for now */}
      {activeTab === 'custom' && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            Custom store hours configuration will be implemented later.
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default StoreHoursDrawer;
